import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { COMMON_AG_GRID_MODULES } from '@/core/tableUtilities/agGrid.constants';
import { AgGridModules } from '@/core/tableUtilities/tableUtilities.types';
import { AssetBuilderGroupHeader } from '@/tableDefinitionEditor/TableDefinitionCellRenderer.atom';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import { BatchActionEnum, TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { ColDef, SizeColumnsToContentStrategy } from 'ag-grid-community';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';

export const SCALING_TABLE_FETCH_CANCELLATION_GROUP = 'scaling-table-fetch';
export const SCALING_TABLE_DEFINITION_CANCELLATION_GROUP = 'scaling-table-definition';
export const ITEM_TABLE_FETCH_CANCELLATION_GROUP = 'item-table-fetch';
export const ITEM_TABLE_DEFINITION_CANCELLATION_GROUP = 'item-table-definition';

export const CSV_TYPES: TableTypeEnum[] = [TableTypeEnum.ITEMCSV, TableTypeEnum.SCALINGCSV];
export const ID_COLUMN_TYPES: string[] = [
  SeeqNames.MaterializedTables.ItemIdColumn,
  SeeqNames.MaterializedTables.CsvItemIdColumn,
];

export const ID_COLUMN: ColumnDefinitionInputV1 = {
  columnName: SeeqNames.MaterializedTables.ItemIdColumn,
  columnType: ColumnTypeEnum.UUID,
  columnRules: [{ eventProperty: { propertyName: 'id' } }],
  sortIndex: 0,
  sortAscending: true,
};

export const DATUM_ID_COLUMN: ColumnDefinitionInputV1 = {
  columnName: SeeqNames.MaterializedTables.DatumIdColumn,
  columnType: ColumnTypeEnum.TEXT,
  columnRules: [{ eventProperty: { propertyName: '' } }],
};

export const DEFAULT_COLUMN_DEFINITION_INPUTS = [ID_COLUMN, DATUM_ID_COLUMN];

export const DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS: string[] = [
  SeeqNames.Properties.DatasourceClass,
  SeeqNames.Properties.Name,
  SeeqNames.Properties.Type,
  SeeqNames.Properties.ValueUom,
];

export const batchActionOptions = [
  {
    value: BatchActionEnum.UPDATEEXISTINGINSERTNEWCLEANUP,
    label: 'SCALING.BATCH_ACTION.OPTIONS.UPDATE_EXISTING_INSERT_NEW_CLEANUP',
  },
  { value: BatchActionEnum.UPDATEEXISTINGINSERTNEW, label: 'SCALING.BATCH_ACTION.OPTIONS.UPDATE_EXISTING_INSERT_NEW' },
  { value: BatchActionEnum.ONLYUPDATEEXISTING, label: 'SCALING.BATCH_ACTION.OPTIONS.ONLY_UPDATE_EXISTING' },
  { value: BatchActionEnum.ONLYINSERTNEW, label: 'SCALING.BATCH_ACTION.OPTIONS.ONLY_INSERT_NEW' },
];

export const MAX_ROW_COUNT = 500000;

export const MODULES = _.concat(COMMON_AG_GRID_MODULES, [
  AgGridModules.ColumnMenuModule,
  AgGridModules.ContextMenuModule,
  AgGridModules.ClipboardModule,
  AgGridModules.CellSelectionModule,
  AgGridModules.ColumnsToolPanelModule,
  AgGridModules.RowGroupingModule,
  AgGridModules.RowDragModule,
  AgGridModules.ColumnApiModule,
  AgGridModules.EventApiModule,
  AgGridModules.CustomEditorModule,
  AgGridModules.TextEditorModule,
  AgGridModules.ClientSideRowModelApiModule,
  AgGridModules.RowGroupingPanelModule,
  // Enables `agGridApi.refreshCells`, which `setEvaluationResults` calls to re-render the rule evaluation details
  // icon after an override. Without this module registered the refresh silently no-ops and the icon never appears.
  AgGridModules.RenderApiModule,
]);

export const GARBAGE_OBJECT_CHAR = '὇B';

export const DEFAULT_COL_DEF: ColDef = {
  menuTabs: ['generalMenuTab'],
  suppressHeaderMenuButton: false,
  headerComponentParams: { menuIcon: 'fa-bars' },
};

/** Debounce window for coalescing fill/paste cell edits into batched override requests. */
export const CELL_OVERRIDE_DEBOUNCE_MS = 200;

export const DEFAULT_ASSET_BUILDER_AUTO_GROUP_COL_DEF: ColDef = {
  minWidth: 200,
  cellRendererParams: {
    innerRenderer: AssetBuilderGroupHeader,
  },
};

export const AUTO_SIZE_STRATEGY: SizeColumnsToContentStrategy = {
  type: 'fitCellContents',
};

export const CONTROL_PANE_DISPLAY = {
  NONE: 'none',
  COLUMN_CONTROL: 'column',
  HIERARCHY_CONTROL: 'hierarchy',
  TEMPLATE_CONTROL: 'template',
} as const;

export const DEFAULT_TABLE_DEFINITION_NAME = 'New Table';

export {
  DISPLAY_NAME_COLUMN_MATCH_SEGMENT,
  DISPLAY_NAME_COLUMN_SUFFIX,
  PROPERTY_COLUMN_MATCH_SEGMENT,
  UOM_COLUMN_MATCH_SUFFIX,
  parseDisplayNameColumnName,
} from '@/tableDefinitionEditor/columnMatch.constants';

export const EDITOR_TYPES = {
  SCALING: 'SCALING',
  ITEM: 'ITEM',
} as const;

export const TABLE_DEFINITION_COLUMN_OPTIONS = {
  ADD_COLUMN: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.ADD_COLUMN', value: 'ADD_COLUMN' },
  ADD_PROPERTY_TO_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.ADD_PROPERTY_TO_COLUMN',
    value: 'ADD_PROPERTY_TO_COLUMN',
  },
  EDIT_COLUMN: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.EDIT_COLUMN', value: 'EDIT_COLUMN' },
  VIEW_COLUMN: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.VIEW_COLUMN', value: 'VIEW_COLUMN' },
  TO_SIMPLE_TABLE: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.TO_SIMPLE_TABLE', value: 'TO_SIMPLE_TABLE' },
  TO_CONDITION_TABLE: { label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.TO_CONDITION_TABLE', value: 'TO_CONDITION_TABLE' },
  CHANGE_DISPLAY_PROPERTY: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CHANGE_DISPLAY_PROPERTY',
    value: 'CHANGE_DISPLAY_PROPERTY',
  },
  CREATE_DESCENDANT_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_DESCENDANT_COLUMN',
    value: 'CREATE_DESCENDANT_COLUMN',
  },
  CREATE_ANCESTOR_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_ANCESTOR_COLUMN',
    value: 'CREATE_ANCESTOR_COLUMN',
  },
  CREATE_PROPERTY_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_PROPERTY_COLUMN',
    value: 'CREATE_PROPERTY_COLUMN',
  },
  CREATE_SHORTCUT_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.CREATE_SHORTCUT_COLUMN',
    value: 'CREATE_SHORTCUT_COLUMN',
  },
  ADD_MANUAL_ENTRY_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.ADD_MANUAL_ENTRY_COLUMN',
    value: 'ADD_MANUAL_ENTRY_COLUMN',
  },
  EDIT_MANUAL_ENTRY_COLUMN: {
    label: 'SCALING.SIDE_PANE.COLUMN_OPTIONS.EDIT_MANUAL_ENTRY_COLUMN',
    value: 'EDIT_MANUAL_ENTRY_COLUMN',
  },
};

export const TABLE_DEFINITION_TEMPLATE_OPTIONS = {
  APPLY_TEMPLATE: { label: 'SCALING.SIDE_PANE.TEMPLATE_OPTIONS.APPLY_TEMPLATE', value: 'APPLY_TEMPLATE' },
  CREATE_TEMPLATE: { label: 'SCALING.SIDE_PANE.TEMPLATE_OPTIONS.CREATE_TEMPLATE', value: 'CREATE_TEMPLATE' },
  EDIT_TEMPLATE: { label: 'SCALING.SIDE_PANE.TEMPLATE_OPTIONS.EDIT_TEMPLATE', value: 'EDIT_TEMPLATE' },
};
